/**
 * UnifiedImageEditor
 *
 * Single-modal image editor combining position (scale-to-fill + pan) and
 * text overlay editing.  Replaces the separate CropAdjustModal + nested
 * TextOverlayEditor with a cohesive, Canva/Figma-inspired experience.
 *
 * Two modes toggled via a segmented control:
 *  - Position: pan the image within the platform frame
 *  - Text:     add/edit/style text layers with Konva on a transparent overlay
 */

import React, {
  useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useReducer,
} from 'react';
import { Stage, Layer, Text, Rect, Transformer, Image as KonvaImage } from 'react-konva';
import type Konva from 'konva';
import {
  X, Move, RotateCcw, Check, Undo2, Redo2,
  Plus, Trash2, Bold, Italic, AlignLeft, AlignCenter, AlignRight,
  Type, Sparkles, Eye, EyeOff, ChevronUp, ChevronDown,
  LayoutTemplate, Image as ImageIcon, ChevronDown as ChevronDownIcon,
  Save, Loader2, ShieldAlert, Square, Circle, Minus,
} from 'lucide-react';

import {
  type TextLayer,
  type OverlayHint,
  type LayerHistory,
  layersReducer,
  FONT_CATEGORIES,
  ALL_FONTS,
  DEFAULT_LAYER_BASE,
  nextId,
  FontPicker,
} from './editorShared';
import {
  EFFECT_PRESETS, DEFAULT_EFFECT_PARAMS, renderEffectCanvas, getKonvaTextEffectProps,
  buildFont, wrapText,
  type EffectId, type EffectParams,
} from './effects/presets';
import { EffectPresetsPanel } from './effects/EffectPresetsPanel';
import { OVERLAY_TEMPLATES, templateLayerToTextLayer, type OverlayTemplate } from './effects/templates';
import type { CropParams, StoredTextLayer, PlatformAdjustment } from './mediaAdjustmentTypes';
import { computeCenterCrop } from './mediaAdjustmentTypes';
import {
  PLATFORM_LIST,
  getPlatformOutputDimensions, getPlatformName, getPlatformIcon,
} from '../ads/utils/platformConfig';
import type { Platform } from '../ads/types';
import { getPlatformSafeZone } from '../ads/utils/platformConfig';
import { renderFinalImage } from './renderFinalImage';

// ─── Types ───────────────────────────────────────────────────────────────────

type EditorMode = 'position' | 'text';

// ─── Discriminated union props ──────────────────────────────────────────────

interface BaseProps {
  sourceImageUrl: string;
  onClose: () => void;
}

interface CampaignProps extends BaseProps {
  editorContext: 'campaign';
  platform: string;
  outputWidth: number;
  outputHeight: number;
  existingAdjustment?: PlatformAdjustment | null;
  overlayHint?: OverlayHint | null;
  onApply: (adjustment: PlatformAdjustment) => void;
}

interface LibraryProps extends BaseProps {
  editorContext: 'library';
  sourceAssetId: string;
  sourceAssetName?: string;
  onSave: (blob: Blob, name: string, hasTextLayers: boolean) => void;
  isSaving?: boolean;
  overlayHint?: OverlayHint | null;
  initialPlatform?: string;
}

type Props = CampaignProps | LibraryProps;

interface FillParams {
  w_frac: number;
  h_frac: number;
  maxPanX: number;
  maxPanY: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function computeScaleToFill(naturalW: number, naturalH: number, targetAR: number): FillParams {
  const { w_frac, h_frac } = computeCenterCrop(naturalW, naturalH, targetAR, 1);
  return { w_frac, h_frac, maxPanX: Math.max(0, 1 - w_frac), maxPanY: Math.max(0, 1 - h_frac) };
}

function cropParamsToPanFrac(crop: CropParams, fill: FillParams): { x: number; y: number } {
  return {
    x: fill.maxPanX > 0 ? clamp(crop.x_frac / fill.maxPanX, 0, 1) : 0.5,
    y: fill.maxPanY > 0 ? clamp(crop.y_frac / fill.maxPanY, 0, 1) : 0.5,
  };
}

/** Convert display-space TextLayer → stored fraction-based StoredTextLayer */
function toStoredLayer(layer: TextLayer, vpW: number, vpH: number): StoredTextLayer {
  return {
    id: layer.id,
    text: layer.text,
    x_frac: layer.x / vpW,
    y_frac: layer.y / vpH,
    w_frac: layer.width / vpW,
    fontSize_vw: layer.fontSize / vpW,
    fontFamily: layer.fontFamily,
    fill: layer.fill,
    fontStyle: layer.fontStyle,
    align: layer.align,
    opacity: layer.opacity,
    rotation: layer.rotation,
    effect: layer.effect,
    effectParams: layer.effectParams as unknown as Record<string, unknown>,
    letterSpacing_vw: layer.letterSpacing / vpW,
    lineHeight: layer.lineHeight,
    bgShape: layer.bgShape,
    bgColor: layer.bgColor,
    bgOpacity: layer.bgOpacity,
    bgPadding_vw: layer.bgPadding / vpW,
  };
}

/** Convert stored fraction-based StoredTextLayer → display-space TextLayer */
function fromStoredLayer(sl: StoredTextLayer, vpW: number, vpH: number): TextLayer {
  return {
    id: sl.id || nextId(),
    text: sl.text,
    x: sl.x_frac * vpW,
    y: sl.y_frac * vpH,
    width: sl.w_frac * vpW,
    fontSize: sl.fontSize_vw * vpW,
    fontFamily: sl.fontFamily,
    fill: sl.fill,
    fontStyle: sl.fontStyle,
    align: sl.align as 'left' | 'center' | 'right',
    opacity: sl.opacity,
    rotation: sl.rotation,
    effect: (sl.effect || 'none') as EffectId,
    effectParams: { ...DEFAULT_EFFECT_PARAMS, ...(sl.effectParams || {}) } as EffectParams,
    letterSpacing: (sl.letterSpacing_vw || 0) * vpW,
    lineHeight: sl.lineHeight || 1.3,
    bgShape: sl.bgShape || 'none',
    bgColor: sl.bgColor || '#000000',
    bgOpacity: sl.bgOpacity ?? 0.6,
    bgPadding: (sl.bgPadding_vw || 0.025) * vpW,
  };
}

// ─── Template Picker (inline) ────────────────────────────────────────────────

const UIE_TEMPLATE_CATEGORIES: { id: OverlayTemplate['category']; label: string }[] = [
  { id: 'promotional', label: 'Promotional' },
  { id: 'bold', label: 'Bold' },
  { id: 'elegant', label: 'Elegant' },
  { id: 'minimal', label: 'Minimal' },
];

const TemplatePicker: React.FC<{
  onApply: (templateId: string) => void;
  onClose: () => void;
}> = ({ onApply, onClose }) => {
  const [activeCategory, setActiveCategory] = useState<OverlayTemplate['category'] | 'all'>('all');
  const visible = activeCategory === 'all'
    ? OVERLAY_TEMPLATES
    : OVERLAY_TEMPLATES.filter(t => t.category === activeCategory);

  return (
    <div
      className="fixed inset-0 z-[1060] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-2xl p-5 w-full max-w-2xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-ink text-sm flex items-center gap-2">
            <LayoutTemplate size={15} className="text-primary" />
            Choose a Template
            <span className="text-[10px] font-normal text-ink-3">({OVERLAY_TEMPLATES.length} templates)</span>
          </h3>
          <button onClick={onClose} className="text-ink-3 hover:text-ink-2">
            <X size={16} />
          </button>
        </div>

        {/* Category filter tabs */}
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {[{ id: 'all' as const, label: 'All' }, ...UIE_TEMPLATE_CATEGORIES].map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                activeCategory === cat.id
                  ? 'bg-primary text-white border-primary'
                  : 'border-line-2 text-ink-2 hover:bg-inset'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-4 gap-2 max-h-80 overflow-y-auto pr-1">
          {visible.map(tpl => (
            <button
              key={tpl.id}
              onClick={() => { onApply(tpl.id); onClose(); }}
              className="flex flex-col items-center gap-1 p-2.5 rounded-xl border-2
                         border-line hover:border-primary
                         dark:hover:border-primary bg-ground
                         text-center transition-all group"
            >
              <span className="text-xl">{tpl.emoji}</span>
              <span className="text-xs font-medium text-ink-2 leading-tight">
                {tpl.name}
              </span>
              <span className="text-[9px] text-ink-3 line-clamp-2 leading-tight">
                {tpl.description}
              </span>
            </button>
          ))}
        </div>

        <p className="text-xs text-ink-3 mt-3 text-center">
          Applying a template replaces all current layers — undo with Ctrl+Z
        </p>
      </div>
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

export const UnifiedImageEditor: React.FC<Props> = (props) => {
  const { sourceImageUrl, onClose } = props;
  const isCampaign = props.editorContext === 'campaign';
  const isLibrary = props.editorContext === 'library';

  // ─── Platform selection (library = mutable, campaign = fixed) ────────────
  const [selectedPlatform, setSelectedPlatform] = useState<string>(
    isCampaign ? props.platform : (props.initialPlatform || 'meta'),
  );
  const [showPlatformPicker, setShowPlatformPicker] = useState(false);

  // Derive output dimensions from platform
  const campaignWidth = isCampaign ? (props as CampaignProps).outputWidth : 0;
  const campaignHeight = isCampaign ? (props as CampaignProps).outputHeight : 0;
  const dims = useMemo(() => {
    if (isCampaign) return { width: campaignWidth, height: campaignHeight };
    return getPlatformOutputDimensions(selectedPlatform as Platform);
  }, [isCampaign, selectedPlatform, campaignWidth, campaignHeight]);

  const outputWidth = dims.width;
  const outputHeight = dims.height;
  const targetAR = outputWidth / outputHeight;
  const platformLabel = getPlatformName(selectedPlatform as Platform);
  const platformIcon = getPlatformIcon(selectedPlatform as Platform);

  const existingAdjustment = isCampaign ? props.existingAdjustment : undefined;

  // ─── Safe zone ─────────────────────────────────────────────────────────────
  const safeZone = useMemo(
    () => getPlatformSafeZone(selectedPlatform as Platform),
    [selectedPlatform],
  );
  const [showSafeZone, setShowSafeZone] = useState(true);

  // ─── Refs ──────────────────────────────────────────────────────────────────
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const textRefs = useRef(new Map<string, Konva.Text | Konva.Image>());

  // ─── State ─────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<EditorMode>('position');
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [panFrac, setPanFrac] = useState<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
  const [viewportSize, setViewportSize] = useState<{ w: number; h: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingText, setEditingText] = useState<{ id: string; text: string } | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // Font loading: force Konva re-render after font files finish loading
  const [fontReady, setFontReady] = useState(0);
  const handleFontChange = useCallback((layerId: string, fontFamily: string) => {
    dispatch({ type: 'UPDATE_LAYER', id: layerId, updates: { fontFamily } });
    // Explicitly load the specific font — this triggers the @fontsource CSS download
    // and returns a promise that resolves only when THAT font is truly available.
    // document.fonts.ready is unreliable because it resolves immediately when no
    // fonts are currently in-flight (the CSS import may not have started yet).
    const weights = ['400', '700'];
    const styles = ['normal', 'italic'];
    const loads = weights.flatMap(w =>
      styles.map(s =>
        document.fonts.load(`${s} ${w} 48px "${fontFamily}"`).catch(() => {})
      )
    );
    Promise.all(loads).then(() => {
      setFontReady(c => c + 1);
    });
  }, []);

  // Drag state
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{ mx: number; my: number; panFrac: { x: number; y: number } } | null>(null);

  // Layers (undo/redo)
  const [layerState, dispatch] = useReducer(layersReducer, {
    past: [],
    present: [],
    future: [],
  } as LayerHistory);
  const textLayers = layerState.present;

  // Track whether initial stored layers have been loaded
  const initialLayersLoaded = useRef(false);

  // Track previous viewport size for rescaling text layers on resize
  const prevViewportRef = useRef<{ w: number; h: number } | null>(null);

  // ─── Fill params ───────────────────────────────────────────────────────────
  const fillParams = useMemo(() => {
    if (!naturalSize) return null;
    return computeScaleToFill(naturalSize.w, naturalSize.h, targetAR);
  }, [naturalSize, targetAR]);

  // ─── Image loading ─────────────────────────────────────────────────────────
  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img || !img.naturalWidth) return;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
  }, []);

  // Restore pan from existing adjustment
  useEffect(() => {
    if (!fillParams || !existingAdjustment?.crop) return;
    setPanFrac(cropParamsToPanFrac(existingAdjustment.crop, fillParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillParams]);

  // Load existing text layers once viewport is known
  useEffect(() => {
    if (initialLayersLoaded.current || !viewportSize) return;
    if (existingAdjustment?.textLayers?.length) {
      const layers = existingAdjustment.textLayers.map(sl =>
        fromStoredLayer(sl, viewportSize.w, viewportSize.h)
      );
      dispatch({ type: 'REPLACE_ALL', layers });
    }
    // Auto-apply overlay hint (from LLM) when no existing layers — both library and campaign mode
    const campaignHint = !isLibrary ? (props as CampaignProps).overlayHint : null;
    if (!existingAdjustment?.textLayers?.length) {
      const hint = isLibrary ? (props as LibraryProps).overlayHint : campaignHint;
      if (hint && naturalSize) {
        const scaleDown = viewportSize.w / naturalSize.w;
        const id = nextId();
        const layer: TextLayer = {
          ...DEFAULT_LAYER_BASE,
          id,
          text: hint.text,
          x: hint.xFrac * viewportSize.w,
          y: hint.yFrac * viewportSize.h,
          width: (hint.widthFrac ?? 0.8) * viewportSize.w,
          fontSize: Math.max(14, hint.fontSizePx * scaleDown),
          fontFamily: hint.fontFamily || 'Arial',
          fill: hint.fill || '#ffffff',
          fontStyle: hint.fontStyle || 'bold',
          align: hint.align || 'center',
        };
        dispatch({ type: 'ADD_LAYER', layer });
        setSelectedId(id);
        setMode('text');
      }
    }
    initialLayersLoaded.current = true;
  }, [viewportSize, existingAdjustment]);

  // ─── Viewport measurement ─────────────────────────────────────────────────
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);

  const measureViewport = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const cs = getComputedStyle(container);
    const maxW = container.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const maxH = container.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
    if (maxW <= 0 || maxH <= 0) return;
    setContainerSize({ w: Math.floor(maxW), h: Math.floor(maxH) });
    let vpW = maxW;
    let vpH = vpW / targetAR;
    if (vpH > maxH) {
      vpH = maxH;
      vpW = vpH * targetAR;
    }
    setViewportSize({ w: Math.floor(vpW), h: Math.floor(vpH) });
  }, [targetAR]);

  useLayoutEffect(() => {
    measureViewport();
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(measureViewport);
    ro.observe(container);
    return () => ro.disconnect();
  }, [measureViewport]);

  // Remeasure when sidebar visibility changes
  useEffect(() => {
    const t = setTimeout(measureViewport, 320); // after transition
    return () => clearTimeout(t);
  }, [sidebarVisible, mode, measureViewport]);

  // ─── Rescale text layers when viewport dimensions change ──────────────────
  useEffect(() => {
    if (!viewportSize) return;
    const prev = prevViewportRef.current;
    prevViewportRef.current = viewportSize;

    // Skip the very first measurement or when no layers exist
    if (!prev || textLayers.length === 0) return;

    // Skip if dimensions haven't actually changed
    if (prev.w === viewportSize.w && prev.h === viewportSize.h) return;

    const scaleX = viewportSize.w / prev.w;
    const scaleY = viewportSize.h / prev.h;

    const resized = textLayers.map(l => ({
      ...l,
      x: l.x * scaleX,
      y: l.y * scaleY,
      width: l.width * scaleX,
      fontSize: l.fontSize * scaleX,
    }));
    dispatch({ type: 'RESIZE_ALL', layers: resized });
  }, [viewportSize]); // intentionally omitting textLayers to avoid feedback loop

  // ─── Image CSS positioning ─────────────────────────────────────────────────
  const imageStyle = useMemo((): React.CSSProperties | null => {
    if (!naturalSize || !fillParams || !viewportSize) return null;
    const imageAR = naturalSize.w / naturalSize.h;
    let imgW: number, imgH: number;
    if (imageAR > targetAR) {
      imgH = viewportSize.h;
      imgW = imgH * imageAR;
    } else {
      imgW = viewportSize.w;
      imgH = imgW / imageAR;
    }
    const overflowX = imgW - viewportSize.w;
    const overflowY = imgH - viewportSize.h;
    return {
      position: 'absolute' as const,
      width: imgW, height: imgH,
      left: -panFrac.x * overflowX,
      top: -panFrac.y * overflowY,
      pointerEvents: 'none' as const,
    };
  }, [naturalSize, fillParams, viewportSize, panFrac, targetAR]);

  // ─── Position mode layout: full image + crop rect overlay ─────────────────
  // Scales the full image to fit the container, then computes where the crop
  // rectangle sits on top of it (from panFrac + fillParams).
  const positionLayout = useMemo(() => {
    if (!naturalSize || !fillParams || !containerSize) return null;
    const imageAR = naturalSize.w / naturalSize.h;
    // Scale full image to fit container (object-fit: contain)
    let imgW: number, imgH: number;
    if (imageAR > containerSize.w / containerSize.h) {
      imgW = containerSize.w;
      imgH = imgW / imageAR;
    } else {
      imgH = containerSize.h;
      imgW = imgH * imageAR;
    }
    // Scale factor: display pixels per natural pixel
    const scale = imgW / naturalSize.w;
    // Crop rect dimensions in display pixels
    const cropW = fillParams.w_frac * naturalSize.w * scale;
    const cropH = fillParams.h_frac * naturalSize.h * scale;
    // Crop rect position from panFrac (0..1 maps across the available pan range)
    const cropX = panFrac.x * fillParams.maxPanX * naturalSize.w * scale;
    const cropY = panFrac.y * fillParams.maxPanY * naturalSize.h * scale;
    // Image offset within the container (centered)
    const imgLeft = (containerSize.w - imgW) / 2;
    const imgTop = (containerSize.h - imgH) / 2;
    return { imgW, imgH, imgLeft, imgTop, cropX, cropY, cropW, cropH, scale };
  }, [naturalSize, fillParams, containerSize, panFrac]);

  // ─── Pan drag handlers (Position mode) ─────────────────────────────────────
  const canPanX = fillParams ? fillParams.maxPanX > 0.001 : false;
  const canPanY = fillParams ? fillParams.maxPanY > 0.001 : false;

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (mode !== 'position' || !fillParams || !naturalSize) return;
    e.preventDefault();
    isDraggingRef.current = true;
    dragStartRef.current = { mx: e.clientX, my: e.clientY, panFrac: { ...panFrac } };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [mode, fillParams, naturalSize, panFrac]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current || !dragStartRef.current || !fillParams || !naturalSize || !positionLayout) return;
    e.preventDefault();
    // How far the crop rect can travel in display pixels
    const maxTravelX = fillParams.maxPanX * naturalSize.w * positionLayout.scale;
    const maxTravelY = fillParams.maxPanY * naturalSize.h * positionLayout.scale;
    const dx = e.clientX - dragStartRef.current.mx;
    const dy = e.clientY - dragStartRef.current.my;
    setPanFrac({
      x: maxTravelX > 0 ? clamp(dragStartRef.current.panFrac.x - dx / maxTravelX, 0, 1) : 0.5,
      y: maxTravelY > 0 ? clamp(dragStartRef.current.panFrac.y - dy / maxTravelY, 0, 1) : 0.5,
    });
  }, [fillParams, naturalSize, positionLayout]);

  const onPointerUp = useCallback(() => {
    isDraggingRef.current = false;
    dragStartRef.current = null;
  }, []);

  // ─── Crop params ───────────────────────────────────────────────────────────
  const currentCropParams = useCallback((): CropParams | null => {
    if (!fillParams) return null;
    return {
      x_frac: panFrac.x * fillParams.maxPanX,
      y_frac: panFrac.y * fillParams.maxPanY,
      w_frac: fillParams.w_frac,
      h_frac: fillParams.h_frac,
    };
  }, [panFrac, fillParams]);

  // ─── Text layer operations ─────────────────────────────────────────────────
  const addTextLayer = useCallback(() => {
    if (!viewportSize) return;
    const id = nextId();
    const layer: TextLayer = {
      ...DEFAULT_LAYER_BASE,
      id,
      x: viewportSize.w * 0.1,
      y: viewportSize.h * 0.4,
      width: viewportSize.w * 0.8,
    };
    dispatch({ type: 'ADD_LAYER', layer });
    setSelectedId(id);
    // Auto-switch to text mode if in position mode
    if (mode === 'position') setMode('text');
  }, [viewportSize, mode]);

  const handleDragEnd = useCallback((id: string, e: Konva.KonvaEventObject<DragEvent>) => {
    dispatch({ type: 'UPDATE_LAYER', id, updates: { x: e.target.x(), y: e.target.y() } });
  }, []);

  const handleTransformEnd = useCallback((id: string) => {
    const node = textRefs.current.get(id);
    if (!node) return;
    const scaleX = node.scaleX();
    dispatch({
      type: 'UPDATE_LAYER', id,
      updates: { width: Math.max(40, node.width() * scaleX), x: node.x(), y: node.y(), rotation: node.rotation() },
    });
    node.scaleX(1);
    node.scaleY(1);
  }, []);

  // Open inline text editing
  const openEdit = useCallback((layer: TextLayer) => {
    setEditingText({ id: layer.id, text: layer.text });
  }, []);

  // Apply template
  const handleApplyTemplate = useCallback((templateId: string) => {
    if (!viewportSize) return;
    const tpl = OVERLAY_TEMPLATES.find(t => t.id === templateId);
    if (!tpl) return;
    const layers = tpl.layers.map(tl => {
      const base = templateLayerToTextLayer(tl, viewportSize.w, viewportSize.h, nextId);
      return {
        ...base,
        effect: tl.effect as EffectId,
        effectParams: { ...DEFAULT_EFFECT_PARAMS, ...(tl.effectParams || {}) },
      } as TextLayer;
    });
    dispatch({ type: 'REPLACE_ALL', layers });
    setSelectedId(layers[0]?.id ?? null);
    setMode('text');
  }, [viewportSize]);

  // ─── Complex effect rendering ──────────────────────────────────────────────
  const complexRenders = useMemo(() => {
    const result: Record<string, ReturnType<typeof renderEffectCanvas>> = {};
    for (const layer of textLayers) {
      const preset = EFFECT_PRESETS.find(p => p.id === layer.effect);
      if (preset?.isComplex) {
        result[layer.id] = renderEffectCanvas({
          text: layer.text, displayWidth: layer.width, fontSize: layer.fontSize,
          fontFamily: layer.fontFamily, fontStyle: layer.fontStyle, fill: layer.fill,
          align: layer.align, effect: layer.effect, effectParams: layer.effectParams,
          bgImage: imgRef.current,
          lineHeight: layer.lineHeight,
          letterSpacing: layer.letterSpacing,
        });
      }
    }
    return result;
  }, [textLayers, naturalSize, fontReady]); // naturalSize triggers re-render after image loads; fontReady after font files load

  // Compute background rect dimensions for text layers — tightly wraps actual text content
  const bgRects = useMemo(() => {
    const result: Record<string, { x: number; y: number; w: number; h: number; radius: number }> = {};
    for (const layer of textLayers) {
      if (layer.bgShape === 'none') continue;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      ctx.font = buildFont(layer.fontStyle, layer.fontSize, layer.fontFamily);
      if (layer.letterSpacing) (ctx as any).letterSpacing = `${layer.letterSpacing}px`;
      const lines = wrapText(ctx, layer.text, layer.width);
      const lh = layer.lineHeight || 1.3;
      const lineH = layer.fontSize * lh;
      // Actual content height: N lines of lineH, minus trailing line-spacing below last line
      const contentH = lines.length * lineH - layer.fontSize * (lh - 1);
      // Actual max line width (tight fit around the text, not the box)
      const maxLineW = Math.max(...lines.map(l => ctx.measureText(l).width), 0);
      const pad = layer.bgPadding;
      const rectH = contentH + pad * 2;
      const rectW = maxLineW + pad * 2;
      // Position bg rect accounting for text alignment within the text box
      let bgX = layer.x - pad;
      if (layer.align === 'center') bgX = layer.x + (layer.width - maxLineW) / 2 - pad;
      else if (layer.align === 'right') bgX = layer.x + layer.width - maxLineW - pad;
      const bgY = layer.y - pad;
      const radius = layer.bgShape === 'pill' ? rectH / 2 : 4;
      result[layer.id] = { x: bgX, y: bgY, w: rectW, h: rectH, radius };
    }
    return result;
  }, [textLayers]);

  // ─── Transformer sync ─────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    if (mode !== 'text' || !selectedId) {
      tr.nodes([]);
      tr.getLayer()?.batchDraw();
      return;
    }
    const node = textRefs.current.get(selectedId);
    if (node) {
      tr.nodes([node]);
      tr.getLayer()?.batchDraw();
    }
  }, [selectedId, textLayers, mode]);

  // ─── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (editingText) return; // don't intercept while editing
      // Don't intercept keys when user is typing in an input/textarea/contenteditable
      const tag = (e.target as HTMLElement)?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
      if (e.key === 'Escape') { onClose(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        if (isInput) return; // let native undo work in inputs
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'REDO' : 'UNDO' });
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        if (isInput) return;
        e.preventDefault();
        dispatch({ type: 'REDO' });
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (isInput) return; // let native delete work in inputs
        if (selectedId && mode === 'text') {
          dispatch({ type: 'DELETE_LAYER', id: selectedId });
          setSelectedId(null);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editingText, selectedId, mode, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close platform picker on outside click
  const platformPickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showPlatformPicker) return;
    const handler = (e: MouseEvent) => {
      if (platformPickerRef.current && !platformPickerRef.current.contains(e.target as Node)) {
        setShowPlatformPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPlatformPicker]);

  // ─── Apply handler (campaign mode) ─────────────────────────────────────────
  const handleApply = useCallback(() => {
    if (!isCampaign) return;
    const crop = currentCropParams();
    if (!crop || !viewportSize) return;
    const storedLayers = textLayers.map(l => toStoredLayer(l, viewportSize.w, viewportSize.h));
    (props as CampaignProps).onApply({ crop, textLayers: storedLayers, targetWidth: outputWidth, targetHeight: outputHeight });
  }, [isCampaign, currentCropParams, textLayers, viewportSize, outputWidth, outputHeight]);

  // ─── Save handler (library mode) ─────────────────────────────────────────
  const handleSaveLibrary = useCallback(async () => {
    if (!isLibrary || !viewportSize || !naturalSize || !imgRef.current) return;
    try {
      const blob = await renderFinalImage({
        sourceImg: imgRef.current,
        naturalSize,
        panFrac,
        targetAR,
        outputWidth,
        outputHeight,
        textLayers,
        viewportSize,
      });
      // Generate a descriptive filename using the source asset's friendly name
      const assetName = isLibrary ? (props as LibraryProps).sourceAssetName : undefined;
      const baseName = (assetName || sourceImageUrl.split('/').pop() || 'image').replace(/\.[^.]+$/, '');
      const platSlug = selectedPlatform.replace(/[^a-z0-9]/gi, '_');
      const fileName = `${baseName}_${platSlug}_${outputWidth}x${outputHeight}.png`;
      (props as LibraryProps).onSave(blob, fileName, textLayers.length > 0);
    } catch (err) {
      console.error('Failed to render final image:', err);
    }
  }, [isLibrary, viewportSize, naturalSize, panFrac, targetAR, outputWidth, outputHeight, textLayers, selectedPlatform, sourceImageUrl]);

  const handleReset = () => {
    setPanFrac({ x: 0.5, y: 0.5 });
    dispatch({ type: 'REPLACE_ALL', layers: [] });
    setSelectedId(null);
  };

  // Selected layer (for sidebar controls)
  const selectedLayer = textLayers.find(l => l.id === selectedId) ?? null;

  const showSidebar = mode === 'text' && sidebarVisible;

  // Source URL with cache-buster (stable per mount to avoid re-fetches)
  const imgSrc = useMemo(() => {
    const sep = sourceImageUrl.includes('?') ? '&' : '?';
    return `${sourceImageUrl}${sep}t=${Date.now()}`;
  }, [sourceImageUrl]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[1040] flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4">
      <div
        className="bg-card w-full max-w-7xl rounded-2xl shadow-2xl
                   border border-line
                   flex flex-col overflow-hidden
                   h-[95vh] md:h-[90vh]"
        style={{ animation: 'scaleIn 0.2s ease-out' }}
      >
        {/* ═══ HEADER ═══ */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-line shrink-0 bg-card">
          {/* Left — icon + title / platform selector */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center shrink-0">
              <ImageIcon size={14} className="text-white" />
            </div>

            {isLibrary ? (
              /* Library mode: platform selector dropdown */
              <div ref={platformPickerRef} className="relative min-w-0">
                <button
                  type="button"
                  onClick={() => setShowPlatformPicker(p => !p)}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg
                             hover:bg-inset transition-colors text-left"
                >
                  <span className="text-base leading-none">{platformIcon}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">
                      {platformLabel}
                    </div>
                    <span className="text-[10px] text-ink-3">
                      {outputWidth} x {outputHeight}
                    </span>
                  </div>
                  <ChevronDown size={14} className={`text-ink-3 transition-transform ${showPlatformPicker ? 'rotate-180' : ''}`} />
                </button>

                {showPlatformPicker && (
                  <div className="absolute z-50 mt-1 left-0 w-64 max-h-80 overflow-y-auto
                                  bg-card border border-line
                                  rounded-xl shadow-2xl py-1">
                    {PLATFORM_LIST.map(p => (
                      <button
                        key={p.key}
                        onClick={() => {
                          setSelectedPlatform(p.key);
                          setShowPlatformPicker(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm transition-colors
                          ${p.key === selectedPlatform
                            ? 'bg-primary/10 text-primary'
                            : 'text-ink-2 hover:bg-inset'}`}
                      >
                        <span className="text-base w-6 text-center shrink-0">{p.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{p.name}</div>
                          <div className="text-[10px] text-ink-3">{p.outputWidth} x {p.outputHeight}</div>
                        </div>
                        {p.key === selectedPlatform && (
                          <Check size={14} className="shrink-0 text-primary" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Campaign mode: static title */
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-ink truncate">
                  Edit Image — {platformLabel}
                </h2>
                <span className="text-[10px] text-ink-3">
                  {outputWidth} x {outputHeight}
                </span>
              </div>
            )}
          </div>

          {/* Center — mode toggle */}
          <div className="hidden sm:flex items-center bg-inset rounded-lg p-0.5">
            <button
              onClick={() => setMode('position')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-medium transition-all
                ${mode === 'position'
                  ? 'bg-card text-ink shadow-sm'
                  : 'text-ink-3 hover:text-ink-2'
                }`}
            >
              <Move size={13} />
              Position
            </button>
            <button
              onClick={() => setMode('text')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-xs font-medium transition-all
                ${mode === 'text'
                  ? 'bg-card text-ink shadow-sm'
                  : 'text-ink-3 hover:text-ink-2'
                }`}
            >
              <Type size={13} />
              Text
              {textLayers.length > 0 && (
                <span className="ml-0.5 w-4 h-4 rounded-full bg-primary text-[10px] text-white flex items-center justify-center font-semibold">
                  {textLayers.length}
                </span>
              )}
            </button>
          </div>

          {/* Safe zone toggle */}
          {safeZone && (
            <button
              onClick={() => setShowSafeZone(v => !v)}
              title={showSafeZone ? 'Hide safe zones' : 'Show safe zones'}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors
                ${showSafeZone
                  ? 'bg-bad/10 text-bad'
                  : 'text-ink-3 hover:text-ink-2 hover:bg-inset'
                }`}
            >
              <ShieldAlert size={13} />
              <span className="hidden sm:inline">Safe zones</span>
            </button>
          )}

          {/* Right — undo/redo + close */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => dispatch({ type: 'UNDO' })}
              disabled={layerState.past.length === 0}
              title="Undo (Ctrl+Z)"
              className="p-1.5 rounded-lg text-ink-3 hover:text-ink-2
                         hover:bg-inset disabled:opacity-30 transition-colors"
            >
              <Undo2 size={15} />
            </button>
            <button
              onClick={() => dispatch({ type: 'REDO' })}
              disabled={layerState.future.length === 0}
              title="Redo (Ctrl+Y)"
              className="p-1.5 rounded-lg text-ink-3 hover:text-ink-2
                         hover:bg-inset disabled:opacity-30 transition-colors"
            >
              <Redo2 size={15} />
            </button>
            <div className="w-px h-5 bg-inset mx-1" />
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-ink-3 hover:text-ink-2
                         hover:bg-inset transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ═══ MOBILE MODE TOGGLE ═══ */}
        <div className="flex sm:hidden items-center justify-center py-1.5 border-b border-line bg-ground">
          <div className="flex items-center bg-inset rounded-lg p-0.5">
            <button
              onClick={() => setMode('position')}
              className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all
                ${mode === 'position'
                  ? 'bg-card text-ink shadow-sm'
                  : 'text-ink-3'}`}
            >
              <Move size={12} /> Position
            </button>
            <button
              onClick={() => setMode('text')}
              className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all
                ${mode === 'text'
                  ? 'bg-card text-ink shadow-sm'
                  : 'text-ink-3'}`}
            >
              <Type size={12} /> Text
              {textLayers.length > 0 && (
                <span className="ml-0.5 w-3.5 h-3.5 rounded-full bg-primary text-[9px] text-white flex items-center justify-center">
                  {textLayers.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ═══ CONTEXTUAL TOOLBAR ═══ */}
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-line bg-ground/50 shrink-0 overflow-x-auto">
          {mode === 'position' ? (
            <>
              <button
                onClick={() => setPanFrac({ x: 0.5, y: 0.5 })}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium
                           text-ink-2 hover:bg-inset transition-colors"
              >
                <RotateCcw size={12} /> Reset
              </button>
              <div className="w-px h-4 bg-inset" />
              <span className="text-[10px] text-ink-3">
                {canPanX && canPanY ? 'Drag to position' : canPanX ? 'Drag left/right' : canPanY ? 'Drag up/down' : 'Image fits perfectly'}
              </span>
            </>
          ) : (
            <>
              <button
                onClick={addTextLayer}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold
                           bg-gradient-to-r from-primary to-primary-dark text-white
                           hover:from-primary-dark hover:to-primary-dark shadow-sm transition-all"
              >
                <Plus size={13} /> Add Text
              </button>
              <button
                onClick={() => setShowTemplatePicker(true)}
                title="Templates"
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium
                           text-ink-2 hover:bg-inset transition-colors"
              >
                <LayoutTemplate size={13} /> Templates
              </button>

              {selectedLayer && (
                <>
                  <div className="w-px h-4 bg-inset" />

                  {/* Bold */}
                  <button
                    onClick={() => {
                      const isBold = selectedLayer.fontStyle.includes('bold');
                      const newStyle = selectedLayer.fontStyle.replace(/bold\s*/g, '').trim();
                      dispatch({
                        type: 'UPDATE_LAYER', id: selectedLayer.id,
                        updates: { fontStyle: isBold ? (newStyle || 'normal') : `bold ${newStyle}`.trim() },
                      });
                    }}
                    className={`p-1.5 rounded-md transition-colors
                      ${selectedLayer.fontStyle.includes('bold')
                        ? 'bg-primary/10 text-primary'
                        : 'text-ink-3 hover:bg-inset'}`}
                    title="Bold"
                  >
                    <Bold size={14} />
                  </button>

                  {/* Italic */}
                  <button
                    onClick={() => {
                      const isItalic = selectedLayer.fontStyle.includes('italic');
                      const newStyle = selectedLayer.fontStyle.replace(/italic\s*/g, '').trim();
                      dispatch({
                        type: 'UPDATE_LAYER', id: selectedLayer.id,
                        updates: { fontStyle: isItalic ? (newStyle || 'normal') : `${newStyle} italic`.trim() },
                      });
                    }}
                    className={`p-1.5 rounded-md transition-colors
                      ${selectedLayer.fontStyle.includes('italic')
                        ? 'bg-primary/10 text-primary'
                        : 'text-ink-3 hover:bg-inset'}`}
                    title="Italic"
                  >
                    <Italic size={14} />
                  </button>

                  <div className="w-px h-4 bg-inset" />

                  {/* Alignment */}
                  {(['left', 'center', 'right'] as const).map(align => (
                    <button
                      key={align}
                      onClick={() => dispatch({ type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { align } })}
                      className={`p-1.5 rounded-md transition-colors
                        ${selectedLayer.align === align
                          ? 'bg-primary/10 text-primary'
                          : 'text-ink-3 hover:bg-inset'}`}
                      title={`Align ${align}`}
                    >
                      {align === 'left' ? <AlignLeft size={14} /> : align === 'center' ? <AlignCenter size={14} /> : <AlignRight size={14} />}
                    </button>
                  ))}

                  <div className="w-px h-4 bg-inset" />

                  {/* Color swatch */}
                  <label className="relative cursor-pointer" title="Text color">
                    <div className="w-6 h-6 rounded-md border-2 border-line-2" style={{ backgroundColor: selectedLayer.fill }} />
                    <input
                      type="color"
                      value={selectedLayer.fill}
                      onChange={e => dispatch({ type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { fill: e.target.value } })}
                      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    />
                  </label>

                  <div className="w-px h-4 bg-inset" />

                  {/* Delete */}
                  <button
                    onClick={() => { dispatch({ type: 'DELETE_LAYER', id: selectedLayer.id }); setSelectedId(null); }}
                    className="p-1.5 rounded-md text-red-400 hover:text-bad hover:bg-bad-soft transition-colors"
                    title="Delete layer"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </>
          )}
        </div>

        {/* ═══ BODY (canvas + sidebar) ═══ */}
        <div className="flex-1 flex overflow-hidden min-h-0">

          {/* Canvas area */}
          <div
            ref={containerRef}
            className="flex-1 bg-inset flex items-center justify-center p-3 lg:p-5 overflow-hidden min-w-0"
          >
            {/* Hidden img for natural size detection */}
            <img
              ref={imgRef}
              src={imgSrc}
              alt="Source"
              crossOrigin="anonymous"
              onLoad={handleImageLoad}
              draggable={false}
              style={{ display: 'none' }}
            />

            {/* ─── UNIFIED LAYOUT: full image + crop overlay (both modes) ─── */}
            {positionLayout && viewportSize && (
              <div
                style={{
                  width: positionLayout.imgW,
                  height: positionLayout.imgH,
                  position: 'relative',
                  touchAction: 'none',
                  cursor: mode === 'position' && (canPanX || canPanY) ? 'grab' : 'default',
                }}
                onPointerDown={mode === 'position' ? onPointerDown : undefined}
                onPointerMove={mode === 'position' ? onPointerMove : undefined}
                onPointerUp={mode === 'position' ? onPointerUp : undefined}
              >
                {/* Full image */}
                <img
                  src={imgSrc}
                  alt="Full"
                  crossOrigin="anonymous"
                  draggable={false}
                  style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }}
                />

                {/* Dim overlay: 4 rects around the crop area */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: positionLayout.cropY, backgroundColor: 'rgba(0,0,0,0.5)' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, top: positionLayout.cropY + positionLayout.cropH, backgroundColor: 'rgba(0,0,0,0.5)' }} />
                <div style={{ position: 'absolute', top: positionLayout.cropY, left: 0, width: positionLayout.cropX, height: positionLayout.cropH, backgroundColor: 'rgba(0,0,0,0.5)' }} />
                <div style={{ position: 'absolute', top: positionLayout.cropY, right: 0, left: positionLayout.cropX + positionLayout.cropW, height: positionLayout.cropH, backgroundColor: 'rgba(0,0,0,0.5)' }} />

                {/* Crop frame border */}
                <div
                  style={{
                    position: 'absolute',
                    left: positionLayout.cropX,
                    top: positionLayout.cropY,
                    width: positionLayout.cropW,
                    height: positionLayout.cropH,
                    border: '2px solid rgba(255,255,255,0.8)',
                    borderRadius: 6,
                    pointerEvents: 'none',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                  }}
                />

                {/* Drag to position indicator (Position mode only) */}
                {mode === 'position' && (canPanX || canPanY) && !isDraggingRef.current && (
                  <div
                    style={{
                      position: 'absolute',
                      left: positionLayout.cropX,
                      top: positionLayout.cropY,
                      width: positionLayout.cropW,
                      height: positionLayout.cropH,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <div className="bg-black/40 backdrop-blur-sm text-white/90 rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-medium">
                      <Move size={14} />
                      <span>Drag to position</span>
                    </div>
                  </div>
                )}

                {/* ─── Konva text overlay + safe zone (positioned over crop area) ─── */}
                <div
                  ref={viewportRef}
                  style={{
                    position: 'absolute',
                    left: positionLayout.cropX,
                    top: positionLayout.cropY,
                    width: positionLayout.cropW,
                    height: positionLayout.cropH,
                    overflow: 'hidden',
                    borderRadius: 6,
                    pointerEvents: mode === 'text' ? 'auto' : 'none',
                  }}
                >
                  <Stage
                    width={positionLayout.cropW}
                    height={positionLayout.cropH}
                    scaleX={positionLayout.cropW / viewportSize.w}
                    scaleY={positionLayout.cropH / viewportSize.h}
                    onClick={(e: Konva.KonvaEventObject<MouseEvent>) => {
                      if (mode === 'text' && e.target === e.currentTarget) setSelectedId(null);
                    }}
                    onTap={(e: Konva.KonvaEventObject<Event>) => {
                      if (mode === 'text' && e.target === e.currentTarget) setSelectedId(null);
                    }}
                  >
                    <Layer key={`text-layer-${fontReady}`}>
                      {textLayers.map(layer => {
                        const preset = EFFECT_PRESETS.find(p => p.id === layer.effect);
                        const isComplex = preset?.isComplex ?? false;
                        const bg = bgRects[layer.id];

                        if (isComplex) {
                          const render = complexRenders[layer.id];
                          if (!render) return null;
                          return (
                            <React.Fragment key={layer.id}>
                              {bg && (
                                <Rect
                                  x={bg.x} y={bg.y} width={bg.w} height={bg.h}
                                  cornerRadius={bg.radius} fill={layer.bgColor}
                                  opacity={layer.bgOpacity} rotation={layer.rotation} listening={false}
                                />
                              )}
                              <KonvaImage
                                id={layer.id}
                                ref={node => { if (node) textRefs.current.set(layer.id, node); else textRefs.current.delete(layer.id); }}
                                image={render.canvas}
                                x={layer.x - render.padX} y={layer.y - render.padY}
                                width={layer.width + render.padX * 2} height={render.displayHeight}
                                opacity={layer.opacity} rotation={layer.rotation}
                                draggable={mode === 'text'}
                                onClick={() => mode === 'text' && setSelectedId(layer.id)}
                                onTap={() => mode === 'text' && setSelectedId(layer.id)}
                                onDblClick={() => mode === 'text' && openEdit(layer)}
                                onDblTap={() => mode === 'text' && openEdit(layer)}
                                onDragEnd={e => handleDragEnd(layer.id, e)}
                                onTransformEnd={() => handleTransformEnd(layer.id)}
                              />
                            </React.Fragment>
                          );
                        }

                        const effectProps = getKonvaTextEffectProps(layer.effect, layer.effectParams, layer.fontSize);
                        return (
                          <React.Fragment key={layer.id}>
                            {bg && (
                              <Rect
                                x={bg.x} y={bg.y} width={bg.w} height={bg.h}
                                cornerRadius={bg.radius} fill={layer.bgColor}
                                opacity={layer.bgOpacity} rotation={layer.rotation} listening={false}
                              />
                            )}
                            <Text
                              id={layer.id}
                              ref={node => { if (node) textRefs.current.set(layer.id, node); else textRefs.current.delete(layer.id); }}
                              text={layer.text} x={layer.x} y={layer.y} width={layer.width}
                              fontSize={layer.fontSize} fontFamily={layer.fontFamily}
                              fill={layer.fill} fontStyle={layer.fontStyle} align={layer.align}
                              opacity={layer.opacity} rotation={layer.rotation}
                              letterSpacing={layer.letterSpacing} lineHeight={layer.lineHeight}
                              draggable={mode === 'text'} wrap="word"
                              onClick={() => mode === 'text' && setSelectedId(layer.id)}
                              onTap={() => mode === 'text' && setSelectedId(layer.id)}
                              onDblClick={() => mode === 'text' && openEdit(layer)}
                              onDblTap={() => mode === 'text' && openEdit(layer)}
                              onDragEnd={e => handleDragEnd(layer.id, e)}
                              onTransformEnd={() => handleTransformEnd(layer.id)}
                              {...effectProps}
                            />
                          </React.Fragment>
                        );
                      })}

                      {mode === 'text' && (
                        <Transformer
                          ref={transformerRef}
                          enabledAnchors={['middle-left', 'middle-right']}
                          rotateEnabled anchorSize={12} anchorCornerRadius={6}
                          anchorFill="#6366f1" anchorStroke="#fff"
                          borderStroke="#6366f1" borderDash={[4, 2]}
                          rotateAnchorOffset={20}
                          boundBoxFunc={(oldBox, newBox) => newBox.width < 40 ? oldBox : newBox}
                        />
                      )}
                    </Layer>
                  </Stage>

                  {/* Safe zone overlay */}
                  {safeZone && showSafeZone && (
                    <div className="absolute inset-0 pointer-events-none z-10">
                      <div
                        className="absolute top-0 left-0 right-0 bg-bad/15 border-b border-red-500/30 border-dashed"
                        style={{ height: `${safeZone.top * 100}%` }}
                      >
                        <span className="absolute bottom-1 left-2 text-[9px] text-red-400/70">Safe zone</span>
                      </div>
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-bad/15 border-t border-red-500/30 border-dashed"
                        style={{ height: `${safeZone.bottom * 100}%` }}
                      >
                        <span className="absolute top-1 left-2 text-[9px] text-red-400/70">Safe zone</span>
                      </div>
                      <div
                        className="absolute top-0 left-0 bottom-0 bg-bad/10"
                        style={{ width: `${safeZone.left * 100}%` }}
                      />
                      <div
                        className="absolute top-0 right-0 bottom-0 bg-bad/10"
                        style={{ width: `${safeZone.right * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ═══ SIDEBAR (Text mode) ═══ */}
          <div
            className={`border-l border-line bg-ground
                        flex flex-col shrink-0 overflow-y-auto transition-all duration-300 ease-in-out
                        ${showSidebar ? 'w-72 lg:w-80 opacity-100' : 'w-0 opacity-0 overflow-hidden'}`}
          >
            {showSidebar && (
              <>
                {/* Layer list */}
                <div className="p-3 border-b border-line">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-3 mb-2">
                    Layers ({textLayers.length})
                  </div>
                  {textLayers.length === 0 ? (
                    <p className="text-xs text-ink-3 italic py-2">No text layers yet. Click "Add Text" to start.</p>
                  ) : (
                    <div className="flex flex-col gap-1 max-h-36 overflow-y-auto">
                      {[...textLayers].reverse().map(layer => (
                        <div
                          key={layer.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedId(layer.id === selectedId ? null : layer.id)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedId(layer.id === selectedId ? null : layer.id); }}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-xs transition-colors w-full cursor-pointer
                            ${layer.id === selectedId
                              ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                              : 'text-ink-2 hover:bg-inset'}`}
                        >
                          <Type size={11} className="shrink-0" />
                          <span className="truncate flex-1" style={{ fontFamily: layer.fontFamily }}>
                            {layer.text.slice(0, 24) || '(empty)'}
                          </span>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                dispatch({ type: 'UPDATE_LAYER', id: layer.id, updates: { opacity: layer.opacity > 0 ? 0 : 1 } });
                              }}
                              className="p-0.5 hover:text-primary transition-colors rounded"
                              title={layer.opacity > 0 ? 'Hide layer' : 'Show layer'}
                            >
                              {layer.opacity > 0 ? <Eye size={11} /> : <EyeOff size={11} className="opacity-40" />}
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); dispatch({ type: 'MOVE_LAYER', id: layer.id, direction: 'forward' }); }}
                              className="p-0.5 hover:bg-inset rounded"
                              title="Move forward"
                            >
                              <ChevronUp size={10} />
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); dispatch({ type: 'MOVE_LAYER', id: layer.id, direction: 'back' }); }}
                              className="p-0.5 hover:bg-inset rounded"
                              title="Move back"
                            >
                              <ChevronDown size={10} />
                            </button>
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                dispatch({ type: 'DELETE_LAYER', id: layer.id });
                                if (selectedId === layer.id) setSelectedId(null);
                              }}
                              className="p-0.5 hover:text-bad transition-colors rounded"
                              title="Delete layer"
                            >
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Style properties (when a layer is selected) */}
                {selectedLayer && (
                  <div className="p-3 flex flex-col gap-3 text-sm">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                      Style
                    </div>

                    {/* Text content */}
                    <div>
                      <label className="block text-xs text-ink-2 mb-1">Text</label>
                      <textarea
                        value={selectedLayer.text}
                        onChange={e => dispatch({ type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { text: e.target.value } })}
                        rows={2}
                        className="w-full text-xs border border-line rounded-lg px-2.5 py-1.5
                                   bg-card text-ink
                                   focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none resize-none"
                      />
                    </div>

                    {/* Font picker */}
                    <FontPicker
                      value={selectedLayer.fontFamily}
                      onChange={ff => handleFontChange(selectedLayer.id, ff)}
                    />

                    {/* Font size */}
                    <div>
                      <label className="block text-xs text-ink-2 mb-1">
                        Size: {Math.round(selectedLayer.fontSize)}px
                      </label>
                      <input
                        type="range"
                        min={12}
                        max={viewportSize ? Math.round(viewportSize.h * 0.5) : 200}
                        value={selectedLayer.fontSize}
                        onChange={e => dispatch({
                          type: 'UPDATE_LAYER', id: selectedLayer.id,
                          updates: { fontSize: Number(e.target.value) },
                        })}
                        className="w-full accent-primary"
                      />
                    </div>

                    {/* Letter Spacing */}
                    <div>
                      <label className="block text-xs text-ink-2 mb-1">
                        Letter Spacing: {selectedLayer.letterSpacing.toFixed(1)}px
                      </label>
                      <input
                        type="range"
                        min={-2} max={20} step={0.5}
                        value={selectedLayer.letterSpacing}
                        onChange={e => dispatch({
                          type: 'UPDATE_LAYER', id: selectedLayer.id,
                          updates: { letterSpacing: Number(e.target.value) },
                        })}
                        className="w-full accent-primary"
                      />
                    </div>

                    {/* Line Height */}
                    <div>
                      <label className="block text-xs text-ink-2 mb-1">
                        Line Height: {selectedLayer.lineHeight.toFixed(1)}x
                      </label>
                      <input
                        type="range"
                        min={0.8} max={3.0} step={0.1}
                        value={selectedLayer.lineHeight}
                        onChange={e => dispatch({
                          type: 'UPDATE_LAYER', id: selectedLayer.id,
                          updates: { lineHeight: Number(e.target.value) },
                        })}
                        className="w-full accent-primary"
                      />
                    </div>

                    {/* Color */}
                    <div>
                      <label className="block text-xs text-ink-2 mb-1">Color</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={selectedLayer.fill}
                          onChange={e => dispatch({ type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { fill: e.target.value } })}
                          className="w-8 h-7 cursor-pointer rounded border border-line-2 p-0.5 bg-card shrink-0"
                        />
                        <input
                          type="text"
                          value={selectedLayer.fill}
                          onChange={e => {
                            if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) {
                              dispatch({ type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { fill: e.target.value } });
                            }
                          }}
                          maxLength={7}
                          className="flex-1 text-xs border border-line rounded-lg px-2 py-1
                                     bg-card text-ink
                                     focus:ring-1 focus:ring-primary focus:outline-none font-mono"
                        />
                      </div>
                    </div>

                    {/* ── Background Shape ── */}
                    <div>
                      <label className="block text-xs text-ink-2 mb-1">Background</label>
                      <div className="flex items-center gap-1 mb-2">
                        {([['none', 'None', Minus], ['rect', 'Rectangle', Square], ['pill', 'Pill', Circle]] as const).map(
                          ([shape, label, Icon]) => (
                            <button
                              key={shape}
                              onClick={() => dispatch({ type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { bgShape: shape } })}
                              title={label}
                              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors
                                ${selectedLayer.bgShape === shape
                                  ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                                  : 'text-ink-3 hover:bg-inset'}`}
                            >
                              <Icon size={12} />
                              {label}
                            </button>
                          ),
                        )}
                      </div>
                      {selectedLayer.bgShape !== 'none' && (
                        <div className="flex flex-col gap-2 pl-1">
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={selectedLayer.bgColor}
                              onChange={e => dispatch({ type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { bgColor: e.target.value } })}
                              className="w-7 h-6 cursor-pointer rounded border border-line-2 p-0.5 bg-card shrink-0"
                            />
                            <span className="text-[10px] text-ink-3 font-mono">{selectedLayer.bgColor}</span>
                          </div>
                          <div>
                            <label className="block text-[10px] text-ink-3 mb-0.5">
                              Bg Opacity: {Math.round(selectedLayer.bgOpacity * 100)}%
                            </label>
                            <input
                              type="range" min={0} max={1} step={0.05}
                              value={selectedLayer.bgOpacity}
                              onChange={e => dispatch({
                                type: 'UPDATE_LAYER', id: selectedLayer.id,
                                updates: { bgOpacity: Number(e.target.value) },
                              })}
                              className="w-full accent-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-ink-3 mb-0.5">
                              Padding: {Math.round(selectedLayer.bgPadding)}px
                            </label>
                            <input
                              type="range" min={4} max={40} step={1}
                              value={selectedLayer.bgPadding}
                              onChange={e => dispatch({
                                type: 'UPDATE_LAYER', id: selectedLayer.id,
                                updates: { bgPadding: Number(e.target.value) },
                              })}
                              className="w-full accent-primary"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Opacity */}
                    <div>
                      <label className="block text-xs text-ink-2 mb-1">
                        Opacity: {Math.round(selectedLayer.opacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min={0} max={1} step={0.01}
                        value={selectedLayer.opacity}
                        onChange={e => dispatch({
                          type: 'UPDATE_LAYER', id: selectedLayer.id,
                          updates: { opacity: Number(e.target.value) },
                        })}
                        className="w-full accent-primary"
                      />
                    </div>

                    {/* Effects */}
                    <EffectPresetsPanel
                      selectedEffect={selectedLayer.effect}
                      effectParams={selectedLayer.effectParams}
                      onEffectChange={effect => dispatch({
                        type: 'UPDATE_LAYER', id: selectedLayer.id, updates: { effect },
                      })}
                      onParamChange={partial => dispatch({
                        type: 'UPDATE_LAYER', id: selectedLayer.id,
                        updates: { effectParams: { ...selectedLayer.effectParams, ...partial } },
                      })}
                    />
                  </div>
                )}

                {!selectedLayer && textLayers.length > 0 && (
                  <div className="p-4 flex flex-col items-center justify-center text-center gap-2 flex-1">
                    <Type size={24} className="text-gray-300" />
                    <p className="text-xs text-ink-3">
                      Select a text layer to edit its properties
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ═══ FOOTER ═══ */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-line shrink-0 bg-card">
          {/* Left — keyboard hint */}
          <div className="hidden md:flex items-center gap-2 text-[10px] text-ink-3">
            {isLibrary && (
              <span className="text-amber-500/80">
                Saved image is flattened and cannot be re-edited
              </span>
            )}
            {!isLibrary && (mode === 'position' ? (
              <span>Drag to adjust visible area</span>
            ) : (
              <span>Drag to reposition &middot; Handles to resize &middot; Ctrl+Z to undo</span>
            ))}
          </div>

          {/* Right — action buttons */}
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                         text-ink-3 hover:text-ink-2
                         transition-colors"
            >
              <RotateCcw size={12} />
              Reset All
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-ink-2
                         border border-line-2 rounded-lg
                         hover:bg-ground transition-colors"
            >
              Cancel
            </button>

            {isCampaign ? (
              <button
                onClick={handleApply}
                disabled={!fillParams}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white
                           bg-gradient-to-r from-primary to-primary-dark rounded-lg
                           hover:from-primary-dark hover:to-primary-dark
                           shadow-sm hover:shadow-md
                           disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Check size={14} />
                Apply
              </button>
            ) : (
              <button
                onClick={handleSaveLibrary}
                disabled={!fillParams || (props as LibraryProps).isSaving}
                title="Saves a new flattened image to your library. The original stays unchanged."
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white
                           bg-gradient-to-r from-primary to-primary-dark rounded-lg
                           hover:from-primary-dark hover:to-primary-dark
                           shadow-sm hover:shadow-md
                           disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {(props as LibraryProps).isSaving ? (
                  <><Loader2 size={14} className="animate-spin" /> Saving...</>
                ) : (
                  <><Save size={14} /> Save</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── Inline text editing — positioned over the canvas text ─── */}
      {editingText && (() => {
        const layer = textLayers.find(l => l.id === editingText.id);
        if (!layer || !viewportSize || !viewportRef.current || !positionLayout) return null;
        const vpRect = viewportRef.current.getBoundingClientRect();
        // Layer coords are in viewportSize space; the crop area is positionLayout.cropW x cropH
        const sx = positionLayout.cropW / viewportSize.w;
        const sy = positionLayout.cropH / viewportSize.h;
        return (
          <div
            className="fixed inset-0 z-[1060]"
            onClick={() => {
              dispatch({ type: 'UPDATE_LAYER', id: editingText.id, updates: { text: editingText.text } });
              setEditingText(null);
            }}
          >
            <textarea
              autoFocus
              value={editingText.text}
              onChange={e => setEditingText({ ...editingText, text: e.target.value })}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === 'Escape') { setEditingText(null); e.stopPropagation(); }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  dispatch({ type: 'UPDATE_LAYER', id: editingText.id, updates: { text: editingText.text } });
                  setEditingText(null);
                }
              }}
              style={{
                position: 'fixed',
                left: vpRect.left + layer.x * sx,
                top: vpRect.top + layer.y * sy,
                width: Math.max(layer.width * sx, 80),
                minHeight: layer.fontSize * sx * (layer.lineHeight || 1.3) * 1.5,
                fontSize: layer.fontSize * sx,
                fontFamily: layer.fontFamily,
                fontWeight: layer.fontStyle.includes('bold') ? 'bold' : 'normal',
                fontStyle: layer.fontStyle.includes('italic') ? 'italic' : 'normal',
                color: layer.fill,
                textAlign: layer.align,
                lineHeight: layer.lineHeight || 1.3,
                letterSpacing: layer.letterSpacing * sx,
                background: 'rgba(0,0,0,0.6)',
                border: '2px solid #6366f1',
                borderRadius: 6,
                padding: '4px 6px',
                outline: 'none',
                resize: 'none',
                overflow: 'hidden',
                zIndex: 1061,
              }}
            />
          </div>
        );
      })()}

      {/* ─── Template picker ─── */}
      {showTemplatePicker && (
        <TemplatePicker
          onApply={handleApplyTemplate}
          onClose={() => setShowTemplatePicker(false)}
        />
      )}

      {/* ─── scaleIn animation ─── */}
      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1);    }
        }
      `}</style>
    </div>
  );
};

export default UnifiedImageEditor;
